import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, CheckCheck, MessageCircle, SendHorizonal } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/user-avatar";
import { useAuth } from "@/hooks/use-auth";
import { azError } from "@/lib/az-errors";
import {
  fetchConversations,
  fetchProfilesByIds,
  fetchThread,
  markThreadRead,
  peerName,
  sendMessage,
} from "@/lib/messages";

export const Route = createFileRoute("/mesajlar")({
  validateSearch: (s: Record<string, unknown>): { user?: string } => ({
    user: typeof s["user"] === "string" ? (s["user"] as string) : "",
  }),
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Mesajlar — Albazar.az" },
      { name: "description", content: "Alıcı və satıcılarla birbaşa yazışın." },
      { property: "og:title", content: "Mesajlar — Albazar.az" },
      { property: "og:description", content: "Alıcı və satıcılarla yazışın." },
    
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Messages,
});

function Messages() {
  const { user, loading } = useAuth();
  const { user: peerParam } = Route.useSearch();
  const peerId = peerParam ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [peerTyping, setPeerTyping] = useState(false);
  const typingChannel = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const conversations = useQuery({
    queryKey: ["messages", "conversations", user?.id],
    queryFn: () => fetchConversations(user!.id),
    enabled: Boolean(user?.id) && !peerId,
    refetchInterval: 15000,
  });

  const thread = useQuery({
    queryKey: ["messages", "thread", user?.id, peerId],
    queryFn: () => fetchThread(user!.id, peerId),
    enabled: Boolean(user?.id && peerId),
    refetchInterval: 8000,
  });

  const peer = useQuery({
    queryKey: ["messages", "peer", peerId],
    queryFn: async () => (await fetchProfilesByIds([peerId])).get(peerId) ?? null,
    enabled: Boolean(peerId),
  });

  const send = useMutation({
    mutationFn: () =>
      sendMessage({ senderId: user!.id, recipientId: peerId, body: text }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["messages"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  useEffect(() => {
    if (user?.id && peerId) void markThreadRead(user.id, peerId);
  }, [user?.id, peerId, thread.data?.length]);

  useEffect(() => {
    if (!user?.id || !peerId) return;
    const room = [user.id, peerId].sort().join("__");
    const channel = supabase.channel(`typing:${room}`, {
      config: { broadcast: { self: false } },
    });
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    channel
      .on("broadcast", { event: "typing" }, (payload) => {
        const from = (payload["payload"] as { from?: string } | undefined)?.from;
        if (from !== peerId) return;
        setPeerTyping(true);
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => setPeerTyping(false), 3000);
      })
      .subscribe();
    typingChannel.current = channel;
    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      typingChannel.current = null;
      void supabase.removeChannel(channel);
    };
  }, [user?.id, peerId]);

  const notifyTyping = () => {
    if (!typingChannel.current || !user?.id) return;
    if (typingTimer.current) return;
    void typingChannel.current.send({
      type: "broadcast",
      event: "typing",
      payload: { from: user.id },
    });
    typingTimer.current = setTimeout(() => {
      typingTimer.current = null;
    }, 1500);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [thread.data?.length]);

  if (!loading && !user) {
    return (
      <div className="container-x py-16 text-center">
        <h1 className="text-2xl font-extrabold">Mesajlar</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Yazışmaları görmək üçün hesabınıza daxil olun.
        </p>
        <Link to="/giris" className="mt-6 inline-block">
          <Button className="h-11 rounded-2xl px-6 font-bold">Daxil ol</Button>
        </Link>
      </div>
    );
  }

  if (peerId) {
    return (
      <div className="container-x flex min-h-[70vh] flex-col py-4">
        <div className="flex items-center gap-3 border-b border-border pb-3">
          <button
            onClick={() => navigate({ to: "/mesajlar", search: { user: "" } })}
            className="grid size-9 place-items-center rounded-full bg-accent"
            aria-label="Geri"
          >
            <ArrowLeft className="size-4" />
          </button>
          <UserAvatar
            name={peerName(peer.data)}
            src={peer.data?.avatar_url ?? null}
            className="size-9"
          />
          <Link
            to="/istifadeci/$id"
            params={{ id: peerId }}
            className="text-sm font-bold hover:underline"
          >
            {peerName(peer.data)}
          </Link>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto py-4">
          {(thread.data ?? []).map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={mine ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    "max-w-[80%] rounded-2xl px-3.5 py-2 text-sm shadow-card " +
                    (mine ? "bg-primary text-primary-foreground" : "bg-card border border-border")
                  }
                >
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  <p className="mt-1 flex items-center justify-end gap-1 text-[10px] opacity-70">
                    {new Date(m.created_at).toLocaleString("az-AZ")}
                    {mine &&
                      (m.read ? (
                        <CheckCheck className="size-3.5" aria-label="Oxundu" />
                      ) : (
                        <Check className="size-3.5" aria-label="Göndərildi" />
                      ))}
                  </p>
                </div>
              </div>
            );
          })}
          {thread.data?.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Hələ mesaj yoxdur. İlk mesajı siz yazın.
            </p>
          )}
          {peerTyping && (
            <p className="px-1 text-xs font-medium text-muted-foreground">yazır…</p>
          )}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) send.mutate();
          }}
          className="sticky bottom-16 flex items-center gap-2 border-t border-border bg-background/95 py-3 backdrop-blur"
        >
          <Input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              notifyTyping();
            }}
            placeholder="Mesaj yazın…"
            className="h-11 rounded-2xl"
          />
          <Button
            type="submit"
            disabled={!text.trim() || send.isPending}
            className="h-11 rounded-2xl px-4 font-bold"
          >
            <SendHorizonal className="size-4" />
          </Button>
        </form>
      </div>
    );
  }

  const list = conversations.data ?? [];

  return (
    <div className="container-x py-10">
      <h1 className="text-3xl font-extrabold tracking-tight">Mesajlar</h1>
      {list.length > 0 ? (
        <ul className="mt-6 grid gap-2">
          {list.map((c) => (
            <li key={c.peerId}>
              <Link
                to="/mesajlar"
                search={{ user: c.peerId }}
                className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated"
              >
                <UserAvatar
                  name={peerName(c.peer)}
                  src={c.peer?.avatar_url ?? null}
                  className="size-11"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{peerName(c.peer)}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.lastMessage.body}</p>
                </div>
                {c.unread > 0 && (
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                    {c.unread}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
      <div className="mt-8 flex flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-card/60 px-6 py-16 text-center shadow-card">
        <span className="grid size-16 place-items-center rounded-2xl bg-accent text-info">
          <MessageCircle className="size-8" />
        </span>
        <h2 className="mt-4 text-lg font-bold">Hələ mesajınız yoxdur</h2>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          Satıcı və ya alıcılarla yazışmağa başlayanda söhbətləriniz avtomatik burada görünəcək.
        </p>
        <Link
          to="/elanlar"
          search={{ q: "", kateqoriya: "", seher: "", sirala: "yeni" }}
          className="mt-5"
        >
          <Button className="h-11 rounded-2xl px-6 font-bold">Elanlara bax</Button>
        </Link>
      </div>
      )}
    </div>
  );
}